import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * WorkspaceTabs — a row of page tabs with optional counts (T-0184).
 *
 * Mirrors `.rq-tabs .tab` in `docs/requirement-library/prototype/
 * selection.css`: 13px labels 24px apart over a hairline, the active tab
 * in ink-900 semibold with an amber underline, and a small count pill.
 * Used by Firm settings → Discovery frameworks (Areas, Interview types,
 * Questionnaires, Rules, History).
 *
 * Accessibility: `role="tablist"` of `role="tab"` buttons with
 * `aria-selected` and `aria-controls`. Left / Right / Home / End move to
 * and select a tab (automatic activation); only the selected tab is a
 * tab stop. Give the content `role="tabpanel"` with the ids from
 * `workspaceTabIds`.
 */

export interface WorkspaceTab<V extends string> {
  value: V;
  label: string;
  count?: number;
}

export interface WorkspaceTabsProps<V extends string> {
  tabs: ReadonlyArray<WorkspaceTab<V>>;
  value: V;
  onChange: (value: V) => void;
  'aria-label': string;
  /** Prefix for the tab and panel ids. */
  idPrefix: string;
  className?: string;
}

/** The ids of a tab and of the panel it controls. */
export function workspaceTabIds(idPrefix: string, value: string): { tab: string; panel: string } {
  return { tab: `${idPrefix}-tab-${value}`, panel: `${idPrefix}-panel-${value}` };
}

export function WorkspaceTabs<V extends string>({
  tabs,
  value,
  onChange,
  'aria-label': ariaLabel,
  idPrefix,
  className,
}: WorkspaceTabsProps<V>) {
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const selected = Math.max(
    0,
    tabs.findIndex((t) => t.value === value),
  );

  const go = (index: number) => {
    const tab = tabs[index];
    if (!tab) return;
    refs.current[index]?.focus();
    onChange(tab.value);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn('flex gap-6 border-b border-ink-100', className)}
      onKeyDown={(e) => {
        const last = tabs.length - 1;
        const at = refs.current.findIndex((el) => el === document.activeElement);
        const from = at >= 0 ? at : selected;
        const next =
          e.key === 'ArrowRight'
            ? (from + 1) % tabs.length
            : e.key === 'ArrowLeft'
              ? (from - 1 + tabs.length) % tabs.length
              : e.key === 'Home'
                ? 0
                : e.key === 'End'
                  ? last
                  : null;
        if (next == null) return;
        e.preventDefault();
        go(next);
      }}
    >
      {tabs.map((tab, i) => {
        const on = i === selected;
        const ids = workspaceTabIds(idPrefix, tab.value);
        return (
          <button
            key={tab.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={ids.tab}
            aria-selected={on}
            aria-controls={ids.panel}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(tab.value)}
            className={cn(
              '-mb-px inline-flex items-center gap-2 border-b-2 pb-2.5 pt-2 text-body-sm',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
              on
                ? 'border-accent-amber-600 font-semibold text-ink-900'
                : 'border-transparent font-medium text-ink-500 hover:text-ink-700',
            )}
          >
            {tab.label}
            {tab.count != null ? (
              <span className="rounded-full bg-ink-100 px-1.5 py-px text-[11px] font-medium text-ink-500">
                {tab.count.toLocaleString()}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

WorkspaceTabs.displayName = 'WorkspaceTabs';
